import { useEffect, useState } from 'react';
import FormControl from '@material-ui/core/FormControl';
import FormHelperText from '@material-ui/core/FormHelperText';
import InputLabel from '@material-ui/core/InputLabel';
import MenuItem from '@material-ui/core/MenuItem';
import Select from '@material-ui/core/Select';
import Typography from '@material-ui/core/Typography';
import Chip from '@material-ui/core/Chip';
import Box from '@material-ui/core/Box';
import { useApi } from '@backstage/core-plugin-api';
import { catalogApiRef } from '@backstage/plugin-catalog-react';

/**
 * K8s Resource 选项
 */
interface K8sResourceOption {
  name: string;
  title: string;
  description: string;
  clusterName: string;
  namespace: string;
  environmentType: string;
  environmentName: string;
  owner: string;
}

/**
 * K8sResourcePicker
 *
 * 自定义字段：从 Backstage Catalog 选择 K8s 环境 Resource（type: k8s-environment）
 *
 * 使用方式：
 * ```yaml
 * k8sResource:
 *   title: K8s 环境
 *   type: string
 *   ui:field: K8sResourcePicker
 *   ui:options:
 *     environmentType: test  # 可选，按环境类型过滤
 * ```
 */
interface K8sResourcePickerProps {
  onChange: (value: string | undefined) => void;
  rawErrors?: string[];
  required?: boolean;
  formData?: string;
  uiSchema?: Record<string, any>;
  idSchema?: { $id: string };
  schema?: { title?: string; description?: string };
}

export const K8sResourcePicker = ({
  onChange,
  rawErrors,
  required,
  formData,
  uiSchema,
  idSchema,
  schema,
}: K8sResourcePickerProps) => {
  const catalogApi = useApi(catalogApiRef);

  const [resources, setResources] = useState<K8sResourceOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>();

  // 从 uiSchema 获取过滤选项
  const environmentTypeFilter = uiSchema?.['ui:options']?.environmentType;

  useEffect(() => {
    const loadResources = async () => {
      try {
        setLoading(true);
        setError(undefined);

        // 从 Catalog 获取所有 K8s Environment Resource 实体
        const response = await catalogApi.getEntities({
          filter: {
            kind: 'Resource',
            'spec.type': 'k8s-environment',
          },
        });

        let resourceList: K8sResourceOption[] = response.items.map(entity => {
          const annotations = entity.metadata.annotations || {};

          return {
            name: entity.metadata.name,
            title: entity.metadata.title || entity.metadata.name,
            description: entity.metadata.description || '',
            clusterName: annotations['hose.io/k8s-cluster'] || '',
            namespace: annotations['hose.io/k8s-namespace'] || '',
            environmentType: annotations['hose.io/environment-type'] || '',
            environmentName: annotations['hose.io/environment-name'] || entity.metadata.title || entity.metadata.name,
            owner: entity.spec?.owner as string || '',
          };
        });

        // 按环境类型过滤
        if (environmentTypeFilter) {
          resourceList = resourceList.filter(
            r => r.environmentType === environmentTypeFilter,
          );
        }

        // 按名称排序
        resourceList.sort((a, b) => a.name.localeCompare(b.name));

        setResources(resourceList);
      } catch (err) {
        setError(
          err instanceof Error ? err.message : 'Failed to load K8s resources',
        );
      } finally {
        setLoading(false);
      }
    };

    loadResources();
  }, [catalogApi, environmentTypeFilter]);

  // 获取环境类型对应的颜色
  const getEnvColor = (
    envType: string,
  ): 'default' | 'primary' | 'secondary' => {
    switch (envType) {
      case 'test':
        return 'default';
      case 'staging':
        return 'primary';
      case 'production':
        return 'secondary';
      default:
        return 'default';
    }
  };

  const fieldId = idSchema?.$id || 'k8s-resource-picker';

  return (
    <FormControl
      margin="normal"
      required={required}
      error={(rawErrors?.length ?? 0) > 0 && !formData}
      fullWidth
    >
      <InputLabel htmlFor={fieldId}>
        {schema?.title || 'K8s 环境'}
      </InputLabel>
      <Select
        id={fieldId}
        value={formData || ''}
        onChange={e => onChange(e.target.value as string)}
        disabled={loading}
      >
        {loading && (
          <MenuItem value="" disabled>
            加载中...
          </MenuItem>
        )}
        {error && (
          <MenuItem value="" disabled>
            加载失败: {error}
          </MenuItem>
        )}
        {!loading && !error && resources.length === 0 && (
          <MenuItem value="" disabled>
            无可用 K8s 环境资源（请先创建）
          </MenuItem>
        )}
        {resources.map(resource => (
          <MenuItem key={resource.name} value={resource.name}>
            <Box display="flex" alignItems="center" width="100%">
              <Chip
                label={resource.environmentType.toUpperCase()}
                size="small"
                color={getEnvColor(resource.environmentType)}
                style={{ marginRight: 12, minWidth: 80 }}
              />
              <Box>
                <Typography variant="body2" style={{ fontWeight: 500 }}>
                  {resource.environmentName}
                </Typography>
                <Typography variant="caption" color="textSecondary">
                  {resource.namespace} @ {resource.clusterName}
                </Typography>
              </Box>
            </Box>
          </MenuItem>
        ))}
      </Select>
      {(rawErrors?.length ?? 0) > 0 && !formData && (
        <FormHelperText error>此字段为必填项</FormHelperText>
      )}
      {formData &&
        (() => {
          const selected = resources.find(r => r.name === formData);
          return selected ? (
            <FormHelperText>
              已选择: {selected.clusterName} 集群、{selected.namespace} 命名空间、{selected.environmentName}
            </FormHelperText>
          ) : null;
        })()}
    </FormControl>
  );
};
